// src/Pages/Landing/BilebandiLanding.jsx
import React from "react";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import PriceInfoBox from "../../components/PriceInfoBox/PriceInfoBox.jsx";
import { trackLandingCta } from "../../utils/analytics";

/**
 * Bilebändi-landing React Shell -sivulle.
 * HUOM: Ei Helmetiä eikä JSON-LD:tä – metat/structured data WP:stä.
 */

export default function BilebandiLanding() {
  return (
    <section className="relative max-w-6xl mx-auto px-4 pt-24 pb-24">
      {/* Hero-paneeli */}
      <header className="rounded-2xl overflow-hidden ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/50 backdrop-blur-md p-8 md:p-12 shadow-lg">
        {/* 🎯 Bilebändi-landing – näkyvä sisältö, metat WP:stä */}
        <h1 className="text-4xl md:text-6xl font-bold leading-tight text-black dark:text-white">
          Bilebändi juhliin – koko Suomi
        </h1>

        <p className="mt-3 text-black/80 dark:text-white/80 text-base md:text-lg max-w-3xl">
          Buukkerista löydät vapaat bilebändit <strong>reaaliaikaisesti</strong>{" "}
          ja saat <strong>tarjouksen</strong> heti sähköpostiisi. Kokonaishinta
          vahvistuu tarjouksessa (sisältää myös matkakulut).
        </p>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <a
            href="/?type=bilebandi&clear=1#tulokset"
            aria-label="Katso vapaat bilebändit"
            onClick={() =>
              trackLandingCta({
                actType: "bilebandi",
                region: "koko_suomi",
                placement: "hero_primary",
              })
            }
          >
            <NeonButton variant="primary" size="md" className="font-semibold">
              Katso vapaat bilebändit
            </NeonButton>
          </a>

          <a
            href="/?type=bilebandi&book=1&clear=1"
            aria-label="Avaa pikahaku"
            onClick={() =>
              trackLandingCta({
                actType: "bilebandi",
                region: "koko_suomi",
                placement: "hero_quick_search",
              })
            }
          >
            <NeonButton variant="secondary" size="md" className="font-semibold">
              Avaa pikahaku
            </NeonButton>
          </a>
        </div>
      </header>

      {/* Hintainfoboksi: bilebändien valtakunnallinen keskihinta */}
      <div className="mt-8">
        <PriceInfoBox
          title="Bilebändien keskihinnat Suomessa – suosituimmat setit"
          types={["bilebandi"]}
          autoTopSets
          topN={3}
          regionId={null} // null = käytä valtakunnallista keskiarvoa
          refreshMs={15 * 60 * 1000} // päivitys 15 min välein
          compact={true}
          nationalLabel="Buukkerin keskihinta"
        />
        <p className="mt-2 text-black/80 dark:text-white/80 text-sm opacity-70">
          Hinta on suuntaa-antava keskiarvo koko Suomesta.{" "}
          <strong>Lopullinen hinta täsmentyy tarjouspyynnössä</strong> ja se
          sisältää myös matkakulut. Hinnat perustuvat Buukkerin bilebändien
          hinnastoihin eri puolilla Suomea – ei yksittäiseen keikkaan tai
          yksittäisen kaupungin nimeen.
        </p>

        {/* Suosituimmat setit – Buukkerin data (tekstinä selitetty) */}
        <div className="mt-4 rounded-2xl p-4 md:p-5 bg-white/60 dark:bg-zinc-900/70 ring-1 ring-black/5 dark:ring-white/10 shadow-md">
          <h2 className="text-xl md:text-2xl font-semibold text-black dark:text-white mb-1">
            Suosituimmat bilebändisetit Buukkerissa
          </h2>
          <p className="text-black/80 dark:text-white/80 text-sm md:text-base">
            Buukkerin hinnoitteludatan perusteella bilebändien yleisimmät
            keikkasetit vuonna 2025 ovat:
            <br />• <strong>2×45 min</strong>
            <br />• <strong>3×45 min</strong>
            <br />• <strong>1×45 min</strong>
          </p>
          <p className="mt-1 text-black/60 dark:text-white/60 text-xs md:text-sm">
            Luvut perustuvat Buukkerissa julkaistujen bilebändien hinnastoihin
            eri puolilla Suomea.
          </p>
        </div>
      </div>

      {/* Miten buukkeri toimii? */}
      <div className="mt-8 rounded-2xl p-6 md:p-8 bg-white/70 dark:bg-zinc-900/60 ring-1 ring-black/10 dark:ring-white/10 shadow-lg">
        <h2 className="text-2xl md:text-3xl font-semibold text-black dark:text-white mb-3">
          Miten Buukkeri toimii?
        </h2>

        <ul className="list-disc pl-5 space-y-1 text-black/80 dark:text-white/80 text-base md:text-lg">
          <li>Valitse päivämäärä → näet vapaat bilebändit.</li>
          <li>
            Lähetä tarjouspyyntö → saat kokonaishinnan ja ohjeet sähköpostiin.
          </li>
          <li>Hyväksy linkistä → maksa varausmaksu → varaus vahvistuu.</li>
          <li>Keikan jälkeen saat palautelinkin ja koodin arviota varten.</li>
        </ul>
      </div>

      {/* Mitä profiilista löytyy? */}
      <div className="mt-8 rounded-2xl p-6 md:p-8 bg-white/70 dark:bg-zinc-900/60 ring-1 ring-black/10 dark:ring-white/10 shadow-lg">
        <h2 className="text-2xl md:text-3xl font-semibold text-black dark:text-white mb-3">
          Mitä esiintyjän profiilista löytyy?
        </h2>

        <p className="text-black/80 dark:text-white/80 text-base md:text-lg">
          Videot, audiot, kuvagalleria, kuvaus, soittimet, lisäpalvelut,
          keikkailualueet ja setit sekä dynaaminen kalenteri.
        </p>
      </div>
    </section>
  );
}
